import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Send } from "lucide-react";
import { toast } from "sonner";

export default function ChatWidget({ salonId, config }) {
  // Dynamic API URL - use correct backend URL based on environment
  // In development (localhost:3000), use backend port 8001
  // In production or external embedding, use current origin (ingress handles /api routing)
  const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const BACKEND_HOST = isDev && window.location.port === '3000' ? 'localhost:8001' : window.location.host;
  const API = `${window.location.protocol}//${BACKEND_HOST}/api`;
  
  // Add pulse animation styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes chat-pulse {
        0%, 100% {
          box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.7);
        }
        50% {
          box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
        }
      }
      .chat-button-pulse {
        animation: chat-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
      }
      
      /* Balloon protection from parent site styles */
      [data-chatity-balloon="true"] * {
        all: revert;
        box-sizing: border-box;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  
  // Read `open=1` query param so the embed.js vanilla loader can request
  // the chat to mount in already-open state (skipping the in-iframe bubble
  // button — the bubble is rendered by embed.js itself, not React).
  const [isOpen, setIsOpen] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('open') === '1';
    } catch (e) {
      return false;
    }
  });
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Messages stored in sessionStorage (persists during browser session)
  const messagesStorageKey = `chatity_messages_${salonId}`;
  const [messages, setMessages] = useState(() => {
    try {
      const saved = sessionStorage.getItem(messagesStorageKey);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading messages from sessionStorage:', error);
      return [];
    }
  });
  
  // Listen for `chatity_force_open` from parent embed.js — used when the
  // user re-opens the chat via the vanilla button and the iframe is reused.
  useEffect(() => {
    const handler = (ev) => {
      if (ev?.data === 'chatity_force_open') setIsOpen(true);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Balloon states
  const [showBalloon, setShowBalloon] = useState(false);
  const balloonShownKey = `chatity_balloon_shown_${salonId}`;
  const balloonTimerRef = useRef(null);
  const autoOpenTimerRef = useRef(null);
  
  // Session ID — persisted in localStorage (shared across tabs, so opening a product
  // link in a new tab keeps the same dialog). Expires after 30 days of inactivity.
  const [sessionId] = useState(() => {
    const storageKey = `chatity_session_${salonId}`;
    const ttl = 30 * 24 * 3600 * 1000;
    let existingSession = null;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = raw.startsWith('{') ? JSON.parse(raw) : { id: raw, ts: Date.now() };
        if (parsed.id && Date.now() - (parsed.ts || 0) < ttl) existingSession = parsed.id;
      }
      if (!existingSession) existingSession = sessionStorage.getItem(storageKey); // migrate older per-tab sessions
    } catch (e) { /* storage blocked */ }

    if (!existingSession) {
      existingSession = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    try { localStorage.setItem(storageKey, JSON.stringify({ id: existingSession, ts: Date.now() })); } catch (e) { /* ignore */ }
    return existingSession;
  });
  
  // Detect user's language from browser
  const [userLanguage] = useState(() => {
    const browserLang = navigator.language || navigator.userLanguage;
    return browserLang.split('-')[0]; // e.g., "en-US" -> "en"
  });
  
  // Capture UTM parameters from URL
  const [utmParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    let utm_source = params.get('utm_source');
    let utm_medium = params.get('utm_medium');
    // Fallback: derive source from the host page referrer forwarded by embed.js
    if (!utm_source) {
      const ref = params.get('ref');
      if (ref) {
        try {
          const host = new URL(ref).hostname.replace(/^(www\.|l\.|m\.|lm\.)/, '');
          if (host) {
            utm_source = host;
            utm_medium = utm_medium || 'referral';
          }
        } catch (e) { /* ignore malformed referrer */ }
      }
    }
    return {
      utm_source,
      utm_medium,
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term')
    };
  });

  const primaryColor = config?.widget_settings?.primary_color || "#000000";
  const secondaryColor = config?.widget_settings?.secondary_color || "#FFFFFF";
  const buttonColor = config?.widget_settings?.button_color || "#000000";
  
  // Multi-language welcome messages
  const welcomeMessages = {
    'ru': config?.widget_settings?.welcome_message || "Здравствуйте! Чем могу вам помочь?",
    'en': "Hello! How can I help you?",
    'es': "¡Hola! ¿Cómo puedo ayudarte?",
    'fr': "Bonjour ! Comment puis-je vous aider ?",
    'de': "Hallo! Wie kann ich Ihnen helfen?",
    'it': "Ciao! Come posso aiutarti?",
    'pt': "Olá! Como posso ajudá-lo?",
    'zh': "您好！我能帮您什么？",
    'ja': "こんにちは！何かお手伝いできますか？",
    'ko': "안녕하세요! 무엇을 도와드릴까요?",
    'ar': "مرحبا! كيف يمكنني مساعدتك؟",
    'tr': "Merhaba! Size nasıl yardımcı olabilirim?",
    'pl': "Cześć! Pomogę Ci umówić się na wizytę. Jaka usługa Cię interesuje?",
    'uk': "Привіт! Я допоможу вам записатися на послугу. Яка послуга вас цікавить?",
  };
  
  const welcomeMessage = welcomeMessages[userLanguage] || welcomeMessages['en'];

  // Multi-language balloon messages (auto-detect language, use config only if custom)
  const defaultBalloonMessages = {
    'ru': "Здравствуйте! Помогу записаться на услугу 👋",
    'en': "Hello! I can help you book an appointment 👋",
    'fr': "Bonjour! Je peux vous aider à prendre rendez-vous 👋",
    'es': "¡Hola! Puedo ayudarte a reservar una cita 👋",
    'de': "Hallo! Ich kann Ihnen helfen, einen Termin zu buchen 👋",
    'it': "Ciao! Posso aiutarti a prenotare un appuntamento 👋",
    'pt': "Olá! Posso ajudá-lo a agendar um horário 👋",
    'zh': "您好！我可以帮您预约 👋",
    'ja': "こんにちは！予約のお手伝いができます 👋",
    'ko': "안녕하세요! 예약을 도와드릴 수 있습니다 👋",
    'ar': "مرحبا! يمكنني مساعدتك في الحجز 👋",
    'tr': "Merhaba! Randevu almanıza yardımcı olabilirim 👋",
    'pl': "Cześć! Mogę pomóc Ci umówić się na wizytę 👋",
    'uk': "Привіт! Допоможу записатися на послугу 👋",
  };
  
  // Use custom message if set and different from default, otherwise use language-specific message
  const configBalloonMessage = config?.widget_settings?.balloon_message;
  const isCustomBalloonMessage = configBalloonMessage && 
                                  configBalloonMessage !== "Здравствуйте! Помогу записаться на услугу 👋";
  
  const balloonMessage = isCustomBalloonMessage 
    ? configBalloonMessage 
    : (defaultBalloonMessages[userLanguage] || defaultBalloonMessages['en']);

  
  // Multi-language bot names with AI prefix
  const botNames = {
    'ru': "AI Ассистент",
    'en': "AI Assistant",
    'fr': "AI Assistant",
    'es': "AI Asistente",
    'de': "AI Assistent",
    'it': "AI Assistente",
    'pt': "AI Assistente",
    'zh': "AI 助理",
    'ja': "AI アシスタント",
    'ko': "AI 어시스턴트",
    'ar': "AI مساعد",
    'tr': "AI Asistan",
    'pl': "AI Asystent",
    'uk': "AI Асистент",
  };
  
  // Use localized bot name if config bot_name is default/generic, otherwise use custom config value
  const configBotName = config?.widget_settings?.bot_name;
  const defaultBotNames = ["Ассистент", "Assistant", "Asistente", "Assistente", "Assistent", "AI Ассистент", "AI Assistant", "AI Asistente", "AI Assistente", "AI Assistent"];
  const botName = (configBotName && !defaultBotNames.includes(configBotName)) 
    ? configBotName 
    : (botNames[userLanguage] || botNames['en']);
  
  // Multi-language input placeholders
  const placeholders = {
    'ru': "Введите сообщение...",
    'en': "Type your message...",
    'es': "Escribe tu mensaje...",
    'fr': "Tapez votre message...",
    'de': "Geben Sie Ihre Nachricht ein...",
    'it': "Digita il tuo messaggio...",
    'pt': "Digite sua mensagem...",
    'zh': "输入您的消息...",
    'ja': "メッセージを入力...",
    'ko': "메시지를 입력하세요...",
    'ar': "اكتب رسالتك...",
    'tr': "Mesajınızı yazın...",
    'pl': "Wpisz swoją wiadomość...",
    'uk': "Введіть повідомлення...",
  };
  
  const inputPlaceholder = placeholders[userLanguage] || placeholders['en'];
  
  // Multi-language chat window titles based on business type (14 languages)
  const businessType = config?.business_type || 'general';
  
  const getTitleByBusinessType = () => {
    const titles = {
      'medical': {
        'ru': "Запись к врачу",
        'en': "Doctor Appointment",
        'fr': "Rendez-vous médical",
        'es': "Cita médica",
        'de': "Arzttermin",
        'it': "Appuntamento medico",
        'pt': "Consulta médica",
        'zh': "医生预约",
        'ja': "医師の予約",
        'ko': "의사 예약",
        'ar': "موعد الطبيب",
        'tr': "Doktor Randevusu",
        'pl': "Wizyta u lekarza",
        'uk': "Запис до лікаря",
      },
      'beauty': {
        'ru': "Онлайн запись",
        'en': "Online Booking",
        'fr': "Réservation en ligne",
        'es': "Reserva en línea",
        'de': "Online-Buchung",
        'it': "Prenotazione online",
        'pt': "Reserva online",
        'zh': "在线预订",
        'ja': "オンライン予約",
        'ko': "온라인 예약",
        'ar': "الحجز عبر الإنترنت",
        'tr': "Online Rezervasyon",
        'pl': "Rezerwacja online",
        'uk': "Онлайн запис",
      },
      'restaurant': {
        'ru': "Бронь столика",
        'en': "Table Reservation",
        'fr': "Réservation de table",
        'es': "Reserva de mesa",
        'de': "Tischreservierung",
        'it': "Prenotazione tavolo",
        'pt': "Reserva de mesa",
        'zh': "餐桌预订",
        'ja': "テーブル予約",
        'ko': "테이블 예약",
        'ar': "حجز الطاولة",
        'tr': "Masa Rezervasyonu",
        'pl': "Rezerwacja stolika",
        'uk': "Бронювання столика",
      },
      'ecommerce': {
        'ru': "Онлайн консультант",
        'en': "Online Consultant",
        'fr': "Consultant en ligne",
        'es': "Consultor online",
        'de': "Online-Berater",
        'it': "Consulente online",
        'pt': "Consultor online",
        'zh': "在线顾问",
        'ja': "オンラインコンサルタント",
        'ko': "온라인 상담원",
        'ar': "مستشار عبر الإنترنت",
        'tr': "Online Danışman",
        'pl': "Konsultant online",
        'uk': "Онлайн консультант",
      },
      'services': {
        'ru': "Онлайн консультант",
        'en': "Online Consultant",
        'fr': "Consultant en ligne",
        'es': "Consultor online",
        'de': "Online-Berater",
        'it': "Consulente online",
        'pt': "Consultor online",
        'zh': "在线顾问",
        'ja': "オンラインコンサルタント",
        'ko': "온라인 상담원",
        'ar': "مستشار عبر الإنترنت",
        'tr': "Online Danışman",
        'pl': "Konsultant online",
        'uk': "Онлайн консультант",
      },
      'general': {
        'ru': "Онлайн запись",
        'en': "Online Booking",
        'fr': "Réservation en ligne",
        'es': "Reserva en línea",
        'de': "Online-Buchung",
        'it': "Prenotazione online",
        'pt': "Reserva online",
        'zh': "在线预订",
        'ja': "オンライン予約",
        'ko': "온라인 예약",
        'ar': "الحجز عبر الإنترنت",
        'tr': "Online Rezervasyon",
        'pl': "Rezerwacja online",
        'uk': "Онлайн запис",
      }
    };
    
    const typeTitle = titles[businessType] || titles['general'];
    return typeTitle[userLanguage] || typeTitle['en'];
  };
  
  const chatTitle = getTitleByBusinessType();
  
  // Multi-language toast messages
  const toastMessages = {
    'ru': "Заявка отправлена!",
    'en': "Request sent!",
    'fr': "Demande envoyée!",
    'es': "¡Solicitud enviada!",
    'de': "Anfrage gesendet!",
    'it': "Richiesta inviata!",
    'pt': "Pedido enviado!",
    'zh': "请求已发送！",
    'ja': "リクエストが送信されました！",
    'ko': "요청이 전송되었습니다!",
    'ar': "تم إرسال الطلب!",
    'tr': "İstek gönderildi!",
    'pl': "Prośba wysłana!",
    'uk': "Заявку надіслано!",
  };
  
  const toastMessage = toastMessages[userLanguage] || toastMessages['en'];
  
  // Convert markdown formatting to HTML (links, bold text)
  const renderMessageWithLinks = (text) => {
    if (!text) return null;
    
    let processedText = text;
    const elements = [];
    let lastIndex = 0;
    
    // Process markdown links [text](url) and plain URLs
    // Combined regex: markdown links OR plain URLs
    const combinedRegex = /\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s\)]+)/g;
    let match;
    
    while ((match = combinedRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        elements.push(text.substring(lastIndex, match.index));
      }
      
      if (match[1] && match[2]) {
        // Markdown link [text](url)
        elements.push(
          <a
            key={match.index}
            href={match[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:opacity-80"
            style={{ color: 'inherit' }}
          >
            {match[1]}
          </a>
        );
      } else if (match[3]) {
        // Plain URL
        elements.push(
          <a
            key={match.index}
            href={match[3]}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:opacity-80"
            style={{ color: 'inherit' }}
          >
            {match[3]}
          </a>
        );
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      elements.push(text.substring(lastIndex));
    }
    
    // Process bold text **text** in all elements
    return elements.map((element, idx) => {
      if (typeof element === 'string') {
        // Split by bold markers
        const boldRegex = /\*\*([^*]+)\*\*/g;
        const parts = [];
        let lastBoldIndex = 0;
        let boldMatch;
        
        while ((boldMatch = boldRegex.exec(element)) !== null) {
          // Add text before bold
          if (boldMatch.index > lastBoldIndex) {
            parts.push(element.substring(lastBoldIndex, boldMatch.index));
          }
          // Add bold text
          parts.push(
            <strong key={`${idx}-${boldMatch.index}`}>
              {boldMatch[1]}
            </strong>
          );
          lastBoldIndex = boldMatch.index + boldMatch[0].length;
        }
        
        // Add remaining text
        if (lastBoldIndex < element.length) {
          parts.push(element.substring(lastBoldIndex));
        }
        
        return parts.length > 0 ? parts : element;
      }
      return element;
    });
  };
  
  // Save messages to sessionStorage whenever they change; refresh the session TTL
  useEffect(() => {
    try {
      sessionStorage.setItem(messagesStorageKey, JSON.stringify(messages));
      if (messages.length > 1) localStorage.setItem(`chatity_session_${salonId}`, JSON.stringify({ id: sessionId, ts: Date.now() }));
    } catch (error) {
      console.error('Error saving messages to sessionStorage:', error);
    }
  }, [messages, messagesStorageKey, salonId, sessionId]);

  // Notify parent window when widget state changes (for pointer-events control)
  useEffect(() => {
    if (window.parent !== window) {
      window.parent.postMessage(isOpen ? 'widget_open' : 'widget_close', '*');
    }
  }, [isOpen]);

  // Balloon and auto-open logic
  useEffect(() => {
    // Don't show if already shown in this session or widget is open
    if (isOpen || sessionStorage.getItem(balloonShownKey)) {
      return;
    }
    
    const balloonEnabled = config?.widget_settings?.balloon_enabled !== false;
    const balloonDelay = (config?.widget_settings?.balloon_delay || 5) * 1000;
    const autoOpenEnabled = config?.widget_settings?.auto_open_enabled || false;
    const autoOpenDelay = (config?.widget_settings?.auto_open_delay || 15) * 1000;
    
    // Show balloon after delay
    if (balloonEnabled) {
      balloonTimerRef.current = setTimeout(() => {
        setShowBalloon(true);
        sessionStorage.setItem(balloonShownKey, 'true');
        
        // Notify parent window to expand iframe for balloon
        if (window.parent !== window) {
          window.parent.postMessage({ 
            type: 'chatity_balloon_show',
            height: 280,
            width: 350
          }, '*');
        }
        
        // Auto-open chat AFTER balloon was shown (if enabled)
        if (autoOpenEnabled) {
          autoOpenTimerRef.current = setTimeout(() => {
            if (!isOpen) {
              setIsOpen(true);
              setShowBalloon(false);
              // Notify parent that balloon is hiding because chat opened
              if (window.parent !== window) {
                window.parent.postMessage({ 
                  type: 'chatity_balloon_hide',
                  height: 200,
                  width: 200
                }, '*');
              }
            }
          }, autoOpenDelay); // autoOpenDelay already in milliseconds
        }
      }, balloonDelay);
    }
    
    // Cleanup timers
    return () => {
      if (balloonTimerRef.current) clearTimeout(balloonTimerRef.current);
      if (autoOpenTimerRef.current) clearTimeout(autoOpenTimerRef.current);
    };
  }, [config, isOpen, balloonShownKey]);
  
  // Handle balloon click
  const handleBalloonClick = () => {
    setShowBalloon(false);
    setIsOpen(true);
    // Notify parent to shrink iframe since balloon is hiding
    if (window.parent !== window) {
      window.parent.postMessage({ 
        type: 'chatity_balloon_hide',
        height: 200,
        width: 200
      }, '*');
    }
    // Clear auto-open timer since user clicked
    if (autoOpenTimerRef.current) {
      clearTimeout(autoOpenTimerRef.current);
    }
  };
  
  // Hide balloon when chat opens
  useEffect(() => {
    if (isOpen) {
      setShowBalloon(false);
      // Notify parent to shrink iframe since balloon is hiding
      if (window.parent !== window) {
        window.parent.postMessage({ 
          type: 'chatity_balloon_hide',
          height: 200,
          width: 200
        }, '*');
      }
      // Clear timers when chat opens
      if (balloonTimerRef.current) clearTimeout(balloonTimerRef.current);
      if (autoOpenTimerRef.current) clearTimeout(autoOpenTimerRef.current);
    }
  }, [isOpen]);


  // Load chat history when widget opens
  useEffect(() => {
    const loadChatHistory = async () => {
      if (isOpen && !historyLoaded && sessionId) {
        try {
          // Try to load from sessionStorage first
          const savedMessages = sessionStorage.getItem(messagesStorageKey);
          if (savedMessages) {
            const parsed = JSON.parse(savedMessages);
            if (parsed && parsed.length > 0) {
              setMessages(parsed);
              console.log(`✅ Loaded ${parsed.length} messages from sessionStorage`);
              setHistoryLoaded(true);
              return;
            }
          }
          
          // If sessionStorage is empty, try loading from backend (last 24h)
          try {
            const response = await axios.get(`${API}/chat/history/${sessionId}`);
            
            if (response.data && response.data.length > 0) {
              // Add welcome message + backend history
              const formattedMessages = [
                {
                  role: "assistant",
                  content: welcomeMessage,
                  timestamp: new Date()
                },
                ...response.data.map(msg => ({
                  role: msg.role,
                  content: msg.content,
                  timestamp: new Date(msg.created_at || msg.timestamp)
                }))
              ];
              
              setMessages(formattedMessages);
              sessionStorage.setItem(messagesStorageKey, JSON.stringify(formattedMessages));
              console.log(`✅ Loaded ${formattedMessages.length} messages from backend`);
            }
          } catch (error) {
            // Backend history not found or error - just continue with empty
            console.log('No backend history found, starting fresh');
          }
          
          setHistoryLoaded(true);
        } catch (error) {
          console.error('Failed to load chat history:', error);
          setHistoryLoaded(true);
        }
      }
    };
    
    loadChatHistory();
  }, [isOpen, historyLoaded, sessionId, API, messagesStorageKey, welcomeMessage]);

  // Add welcome message only if no history loaded
  useEffect(() => {
    if (isOpen && messages.length === 0 && historyLoaded) {
      // Add welcome message only if no history exists
      setMessages([{
        role: "assistant",
        content: welcomeMessage,
        timestamp: new Date()
      }]);
    }
    
    // Focus input when chat opens
    if (isOpen) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 300);
    }
  }, [isOpen, messages.length, historyLoaded, welcomeMessage]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMsg = {
      role: "user",
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    const currentMessage = inputMessage;
    setInputMessage("");
    setLoading(true);

    try {
      // Use API for all messages
      console.log('ChatWidget: Sending message to API:', API);
      console.log('ChatWidget: Payload:', {
        salon_id: salonId,
        session_id: sessionId,
        message: currentMessage,
        user_language: userLanguage
      });
      
      // Get client timezone info
      const timezoneOffset = new Date().getTimezoneOffset(); // in minutes (negative for ahead of UTC)
      const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      const response = await axios.post(`${API}/chat/message`, {
        salon_id: salonId,
        session_id: sessionId,
        message: currentMessage,
        user_language: userLanguage,
        client_timezone: clientTimezone,
        client_timezone_offset: -timezoneOffset, // Convert to positive for ahead of UTC
        ...utmParams
      });

      // Check if response has valid data
      if (response.data && response.data.message) {
        const assistantMsg = {
          role: "assistant",
          content: response.data.message,
          timestamp: new Date()
        };

        setMessages(prev => [...prev, assistantMsg]);

        if (response.data.booking_complete) {
          toast.success(toastMessage);
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('ChatWidget: Error sending message:', error);
      console.error('ChatWidget: Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config
      });
      
      // Only show error if it's a real error (not just booking completion)
      if (!error.response?.data?.booking_complete) {
        toast.error("Ошибка отправки сообщения");
      }
    } finally {
      setLoading(false);
      // Return focus to input after sending
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  return (
    <>
      {/* Balloon Tooltip - Isolated from parent site styles */}
      {!isOpen && showBalloon && (
        <div
          data-chatity-balloon="true"
          style={{ 
            // Reset all possible inherited styles
            all: 'initial',
            // Position
            position: 'fixed',
            bottom: '112px',
            right: '16px',
            // Layout
            display: 'block',
            boxSizing: 'border-box',
            width: 'auto',
            maxWidth: '280px',
            minWidth: '200px',
            // Styling
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            border: `2px solid ${primaryColor}`,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            padding: '32px 20px 20px 20px',
            // Behavior
            cursor: 'pointer',
            zIndex: 2147483647,
            animation: 'bounce 1s ease-in-out infinite',
            // Typography
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: '14px',
            lineHeight: '1.5',
            color: '#000000',
            // Mobile responsive
            ...(window.innerWidth < 768 && {
              bottom: '128px',
              right: '24px',
            })
          }}
          onClick={handleBalloonClick}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowBalloon(false);
              // Notify parent to shrink iframe when closing balloon
              if (window.parent !== window) {
                window.parent.postMessage({ 
                  type: 'chatity_balloon_hide',
                  height: 200,
                  width: 200
                }, '*');
              }
            }}
            style={{ 
              // Reset
              all: 'initial',
              // Position
              position: 'absolute',
              top: '8px',
              right: '8px',
              // Layout
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              // Styling
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '0',
              padding: '0',
              margin: '0',
              // Typography
              fontSize: '24px',
              lineHeight: '24px',
              fontWeight: 'bold',
              color: '#9ca3af',
              fontFamily: 'inherit',
              // Behavior
              cursor: 'pointer',
              outline: 'none',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.color = '#4b5563'}
            onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
          >
            ×
          </button>
          <p style={{ 
            all: 'initial',
            display: 'block',
            margin: '0',
            padding: '0',
            fontSize: '14px',
            lineHeight: '1.5',
            fontWeight: '500',
            color: primaryColor,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}>
            {balloonMessage}
          </p>
          {/* Arrow pointing to button */}
          <div
            style={{
              all: 'initial',
              position: 'absolute',
              bottom: '-10px',
              right: '32px',
              width: '0',
              height: '0',
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              borderTop: `10px solid ${primaryColor}`,
            }}
          />
        </div>
      )}

      {/* Chat Button - Always visible when closed */}
      {!isOpen && (
        <button
          data-testid="chat-widget-open-button"
          onClick={() => setIsOpen(true)}
          className="chat-button-pulse fixed bottom-2 right-2 rounded-full p-5 md:p-6 shadow-2xl hover:scale-105 border-4 z-[9999]"
          style={{
            backgroundColor: buttonColor,
            borderColor: secondaryColor,
            transition: 'all 0.3s ease'
          }}
        >
          <MessageCircle className="w-12 h-12 md:w-14 md:h-14" style={{ color: secondaryColor }} />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          data-testid="chat-widget-window"
          className="fixed inset-0 md:bottom-0 md:right-0 md:inset-auto w-full h-full md:w-96 md:h-[600px] shadow-2xl rounded-none md:rounded-2xl overflow-hidden border-4 md:border-4 border-0 flex flex-col z-[9999]"
          style={{
            backgroundColor: secondaryColor,
            borderColor: primaryColor,
          }}
        >
          {/* Header - Mobile optimized */}
          <div
            className="p-4 md:p-4 flex justify-between items-center border-b-4"
            style={{
              backgroundColor: primaryColor,
              borderColor: secondaryColor
            }}
          >
            <div>
              <h3 className="font-bold text-xl md:text-lg" style={{ color: secondaryColor }}>
                {chatTitle}
              </h3>
              <p className="text-sm md:text-xs" style={{ color: secondaryColor, opacity: 0.8 }}>
                {botName}
              </p>
            </div>
            <button
              data-testid="chat-widget-close-button"
              onClick={() => {
                setIsOpen(false);
                if (window.parent !== window) {
                  window.parent.postMessage('widget_close', '*');
                }
              }}
              className="hover:opacity-70 transition-opacity p-2"
            >
              <X className="w-7 h-7 md:w-6 md:h-6" style={{ color: secondaryColor }} />
            </button>
          </div>

          {/* Messages - Mobile optimized.
              `min-h-0` is critical inside flex-col so this child can shrink
              and not push the input form below the viewport. */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-4 space-y-4 md:space-y-3" style={{ backgroundColor: secondaryColor }}>
            {messages.map((msg, index) => (
              <div
                key={index}
                data-testid={`chat-message-${index}`}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] md:max-w-[80%] p-4 md:p-3 rounded-lg ${msg.role === 'user' ? 'rounded-br-none' : 'rounded-bl-none'}`}
                  style={{
                    backgroundColor: msg.role === 'user' ? primaryColor : '#f0f0f0',
                    color: msg.role === 'user' ? secondaryColor : primaryColor
                  }}
                >
                  <p className="text-base md:text-sm whitespace-pre-wrap break-words leading-relaxed">
                    {renderMessageWithLinks(msg.content)}
                  </p>
                  <p className="text-xs md:text-xs mt-1.5 md:mt-1 opacity-60">
                    {new Date(msg.timestamp).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div
                  className="p-4 md:p-3 rounded-lg rounded-bl-none"
                  style={{ backgroundColor: '#f0f0f0' }}
                >
                  <div className="flex space-x-2">
                    <div className="w-2.5 h-2.5 md:w-2 md:h-2 rounded-full animate-bounce" style={{ backgroundColor: primaryColor, animationDelay: '0ms' }}></div>
                    <div className="w-2.5 h-2.5 md:w-2 md:h-2 rounded-full animate-bounce" style={{ backgroundColor: primaryColor, animationDelay: '150ms' }}></div>
                    <div className="w-2.5 h-2.5 md:w-2 md:h-2 rounded-full animate-bounce" style={{ backgroundColor: primaryColor, animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Demo examples removed - widget now uses API only */}

          {/* Input — sticks to the bottom regardless of viewport changes.
              `flex-shrink-0` prevents the form from collapsing when the
              messages list grows. `safe-area-inset-bottom` keeps the input
              above iPhone home indicator / Android gesture bar. */}
          <form
            onSubmit={handleSendMessage}
            className="flex-shrink-0 p-4 pb-3 md:pb-2 border-t-2"
            style={{
              borderColor: primaryColor,
              paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))',
            }}
          >
            <div className="flex gap-2 md:gap-2">
              <Input
                ref={inputRef}
                data-testid="chat-input"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onFocus={() => {
                  // iOS Safari hack: when the soft keyboard opens, the visual
                  // viewport shrinks and iOS tries to scroll the focused input
                  // into view — but for a position:fixed iframe this can push
                  // the chat off-screen. We compensate by scrolling our own
                  // messages container to the bottom (keeps the latest reply
                  // and input both visible inside the iframe) after the
                  // keyboard animation settles (~300ms).
                  setTimeout(() => {
                    try {
                      messagesEndRef.current?.scrollIntoView({ block: 'end', inline: 'nearest' });
                    } catch (e) { /* no-op */ }
                  }, 300);
                }}
                placeholder={inputPlaceholder}
                disabled={loading}
                className="flex-1 border-2 text-base md:text-sm h-12 md:h-10"
                style={{ 
                  borderColor: primaryColor,
                  color: primaryColor,
                  backgroundColor: secondaryColor,
                  fontSize: '16px' // Prevents iOS zoom on focus
                }}
              />
              <Button
                data-testid="chat-send-button"
                type="submit"
                disabled={loading || !inputMessage.trim()}
                className="px-4 md:px-4 h-12 md:h-10 min-w-[48px]"
                style={{
                  backgroundColor: primaryColor,
                  color: secondaryColor
                }}
              >
                <Send className="w-6 h-6 md:w-5 md:h-5" />
              </Button>
            </div>
            
            {/* GDPR notice: business = controller, Chatity = processor */}
            <p className="text-[10px] leading-snug opacity-60 mt-2 text-center" style={{ color: primaryColor }} data-testid="widget-privacy-notice">
              {({
                ru: `Данные обрабатывает ${config?.privacy?.controller_name || config?.salon_name || ''}; Chatity — технический провайдер. `,
                fr: `Vos données sont traitées par ${config?.privacy?.controller_name || config?.salon_name || ''} ; Chatity agit en tant que prestataire technique. `,
                tr: `Verileriniz ${config?.privacy?.controller_name || config?.salon_name || ''} tarafından işlenir; Chatity teknik sağlayıcıdır. `,
              })[userLanguage] || `Your data is processed by ${config?.privacy?.controller_name || config?.salon_name || ''}; Chatity acts as technical provider. `}
              <a href={config?.privacy?.privacy_policy_url || 'https://chatity.io/privacy'} target="_blank" rel="noopener noreferrer" className="underline" data-testid="widget-privacy-link">
                {({ ru: 'Конфиденциальность', fr: 'Confidentialité', tr: 'Gizlilik' })[userLanguage] || 'Privacy'}
              </a>
            </p>
            {/* Powered by Chatity.io - Smaller on mobile */}
            <div className="text-center mt-1">
              <a
                href="https://chatity.io?utm_source=cw"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] md:text-xs opacity-50 md:opacity-60 hover:opacity-100 transition-opacity"
                style={{ color: primaryColor }}
              >
                Powered by Chatity.io
              </a>
            </div>
          </form>
        </div>
      )}
    </>
  );
}